import {
    Component,
    forceUpdate,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
    State,
} from "@stencil/core";
import { getCountryName, i18nProvider } from "@trumpf/ux.i18n";
import { fetchData } from "./api";
import { getDataFromScriptTag } from "./utils";
import {
    META_NAV_ID_HOME,
    META_NAV_ID_LOCALE,
    META_NAV_ID_PROFILE,
} from "./constants";

/**
 * @component ux-iws-sf-page-header
 * @description
 *   Component for the page header with navigation, logo, profile, and country selection.
 *   Supports internationalization and dynamic loading of navigation data.
 *
 * @example
 * <ux-iws-sf-page-header
 *   endpoint="/api/navigation"
 *   locale="de_DE"
 *   logo-link-url="/"
 *   logo-link-text="Trumpf"
 *   home-link-url="/home"
 *   home-link-text="Home"
 *   profile-link-text="Profile"
 * ></ux-iws-sf-page-header>
 */
@Component({
    tag: "ux-iws-sf-page-header",
    styleUrl: "sf-page-header.scss",
    shadow: false,
})
export class SfPageHeader {
    /** Static cache for loaded navigation HTML (shared across all instances) */
    private static cachedNavigationHtml: string = "";
    /** ID for the i18n provider subscription */
    private i18nSubscriberId: string = "";
    /** Counter for navigation updates (used to refresh the key) */
    private navigationDataIncrement = 0;

    /** Reference to the host element */
    @Element() host: HTMLUxIwsSfPageHeaderElement;

    /** API endpoint used to fetch the navigation */
    @Prop() endpoint: string = "";

    /** Locale used to fetch data and translate texts */
    @Prop() locale: string = "de_DE";

    /** Link url for the TRUMPF logo */
    @Prop() logoLinkUrl: string = "/";

    /** Link text for the TRUMPF logo */
    @Prop() logoLinkText: string = "Trumpf";

    /** Link url for the site menu item */
    @Prop() homeLinkUrl: string = "";

    /** Link text for the site menu item */
    @Prop() homeLinkText: string = "";

    /** Text for the profile menu item */
    @Prop() profileLinkText: string = "";

    /** Links shown in the profile meta-nav popover */
    @Prop({ mutable: true }) profileLinks: Array<{
        label: string;
        href: string;
    }> = [];

    /** Storage for the fetched navigation HTML */
    @State() navigationHtml: string = "";

    /** which languageSwitch is open (locale-selection-desktop or locale-selection-mobile) */
    @State() languageSwitchOpen: string = "";

    /**
     * Custom event that is emitted when a meta nav item is clicked.
     * The `event.detail` provides additional info about the clicked nav item
     * (e.g. "locale-selection", "profile")
     */
    @Event({
        eventName: "uxMetaNavClick",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    metaNavClickEvent: EventEmitter<string>;

    /**
     * Custom event that is emitted when a link is clicked (e.g. in the profile meta-nav popover).
     * The `event.detail` provides the href of the clicked link.
     */
    @Event({
        eventName: "uxLinkClick",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    linkClickEvent: EventEmitter<string>;

    componentWillLoad() {
        if (this.locale) {
            i18nProvider.changeLanguage(this.locale.replace("_", "-"));
        }
        if (this.profileLinks.length === 0) {
            const profileLinks =
                getDataFromScriptTag<Array<{ label: string; href: string }>>(
                    "profileLinks"
                ) || [];
            if (profileLinks.length > 0) {
                this.profileLinks = profileLinks;
            }
        }
        if (SfPageHeader.cachedNavigationHtml) {
            this.navigationDataIncrement++;
            this.navigationHtml = SfPageHeader.cachedNavigationHtml;
        }
        if (this.endpoint) {
            fetchData(this.endpoint, this.locale).then((html) => {
                this.navigationDataIncrement++;
                this.navigationHtml = html;
                SfPageHeader.cachedNavigationHtml = html;
            });
        }
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    /**
     * Returns the country code and name for the current locale.
     * @returns {string} Country name and code (e.g. "Germany | DE")
     */
    private getCountryLabel = (): string => {
        const parts = this.locale.replace("-", "_").split("_");
        const countryCode = parts[1].toUpperCase();
        const countryName = getCountryName(countryCode) || countryCode;
        return `${countryName} | ${parts[0]}`;
    };

    /**
     * Handles clicks on meta-nav items (e.g. profile or locale selection).
     * Locates the closest ancestor with a `data-id` attribute and emits the
     * `uxMetaNavClick` event ith the value of that attribute as detail.
     * Prevents the default link behavior if a target is found.
     */
    private handleClickMetaNavLink = (event: Event) => {
        const target = event.target as HTMLElement;
        const targetWithDataId = target.closest("[data-id]");
        if (!targetWithDataId) {
            return;
        }
        const dataId = targetWithDataId.getAttribute("data-id");
        if (dataId === META_NAV_ID_LOCALE) {
            event.preventDefault();
            this.languageSwitchOpen = targetWithDataId.getAttribute("id");
        }
        if (this.metaNavClickEvent.emit(dataId).defaultPrevented) {
            event.preventDefault();
            return;
        }
    };

    /**
     * Renders a meta-nav link with icon and label.
     * Used for items like profile or country selection in the meta-nav.
     *
     * @param id - The ID of the meta-nav item (e.g. "profile" or "locale-selection").
     * @param label - The text to display for the link.
     * @param url - The URL to navigate to when the link is clicked.
     * @param iconName - The name of the icon to display.
     */
    private renderMetaNavLink = (
        id: string,
        label: string,
        url: string,
        iconName: string,
        uniqueId?: string
    ) => {
        return (
            <a
                href={url}
                aria-label={label}
                data-id={id}
                id={uniqueId}
                onClick={this.handleClickMetaNavLink}
            >
                {iconName && (
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name={iconName}></ux-icon>
                    </span>
                )}
                <span class="ux-iws-navigation-meta__link-text">{label}</span>
            </a>
        );
    };

    /**
     * Renders the country selection meta-nav item.
     */
    private renderCountryLink = (renderLocation: "desktop" | "mobile") => {
        const label = this.getCountryLabel();
        const id = `${META_NAV_ID_LOCALE}-${renderLocation}`;
        if (!label) {
            return null;
        }
        return (
            <div class="ux-iws-navigation-meta__item">
                {this.renderMetaNavLink(
                    META_NAV_ID_LOCALE,
                    label,
                    `#/${META_NAV_ID_LOCALE}`,
                    "nav-globe",
                    id
                )}
                <ux-iws-sf-language-switch
                    onUxClose={() => (this.languageSwitchOpen = "")}
                    onUxChangeLanguage={() => {
                        this.languageSwitchOpen = "";
                    }}
                    language={this.locale}
                    opened={this.languageSwitchOpen === id}
                    targetId={id}
                ></ux-iws-sf-language-switch>
            </div>
        );
    };

    /**
     * Renders the profile link with tooltip.
     * @param popoverPlacement - Placement of the profile link popover.
     */
    private renderProfileLink = (
        popoverPlacement: "top" | "bottom" = "top"
    ) => {
        const label = this.profileLinkText;
        if (!label) {
            return null;
        }
        return (
            <div class="ux-iws-navigation-meta__item">
                {this.renderMetaNavLink(
                    META_NAV_ID_PROFILE,
                    label,
                    `#/${META_NAV_ID_PROFILE}`,
                    "avatar-circle"
                )}
                {this.renderProfileLinkDropdown(popoverPlacement)}
            </div>
        );
    };

    /**
     * Renders the dropdown with profile links as a tooltip.
     * Each link in `profileLinks` is rendered as an anchor element inside the tooltip.
     * Emits the `uxLinkClick` event when a link is clicked.
     * Returns `null` if there are no profile links.
     *
     * @param popoverPlacement - Placement of the profile link popover.
     */
    private renderProfileLinkDropdown = (
        popoverPlacement: "top" | "bottom"
    ) => {
        if (
            !Array.isArray(this.profileLinks) ||
            this.profileLinks.length === 0
        ) {
            return null;
        }
        return (
            <ux-tooltip
                tooltip-target="[data-id='profile']"
                align={popoverPlacement}
                onClick={(event: MouseEvent) => {
                    // Prevents the tooltip from closing when clicking inside it
                    event.stopPropagation();
                }}
            >
                <button class="button-close" data-ux-tooltip-close="">
                    <ux-icon name="close" class="ux-icon" />
                </button>
                {this.profileLinks.map((link) => (
                    <a
                        key={link.href}
                        class="ux-tooltip__link-item"
                        href={link.href}
                        onClick={(event: MouseEvent) => {
                            if (
                                this.linkClickEvent.emit(link.href)
                                    .defaultPrevented
                            ) {
                                event.preventDefault();
                            }
                        }}
                    >
                        {link.label}
                    </a>
                ))}
            </ux-tooltip>
        );
    };

    /**
     * Renders the home link, optionally as a dropdown variant.
     * @param {boolean} [dropdown=false] - Whether to render the dropdown variant.
     * @returns {JSX.Element | null}
     * @private
     */
    private renderHomeLink = (dropdown: boolean = false) => {
        if (!(this.homeLinkUrl && this.homeLinkText)) {
            return null;
        }
        return dropdown ? (
            <div class="ux-iws-navigation-meta__item">
                <a class="ux-button ux-button__trumpf" href={this.homeLinkUrl}>
                    {this.homeLinkText}
                    <ux-icon name="chevron-right"></ux-icon>
                </a>
            </div>
        ) : (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item--top-border">
                {this.renderMetaNavLink(
                    META_NAV_ID_HOME,
                    this.homeLinkText,
                    this.homeLinkUrl,
                    "nav-home"
                )}
            </div>
        );
    };

    render() {
        const { t } = i18nProvider;
        const menuLabel = t("app.header.menuLabel", "Menu");
        const closeLabel = t("app.header.closeButton", "Close");
        const backLabel = t("general.prevButton", "Back");
        return (
            <Host>
                <ux-iws-header type="mytrumpf">
                    <div slot="header" class="ux-iws-header__main-wrapper">
                        <a
                            class="ux-iws-header__logo"
                            href={this.logoLinkUrl}
                            aria-label={this.logoLinkText}
                        >
                            <img
                                src="https://www.trumpf.com/typo3conf/ext/trumpf_package/Resources/Public/Images/nav/logo.png"
                                alt="Trumpf logo"
                            />
                        </a>
                        <div class="ux-iws-header__navigation-wrapper">
                            <ux-iws-nav
                                back-button-text={backLabel}
                                key={
                                    this.navigationHtml
                                        ? `nav-${this.navigationDataIncrement}`
                                        : "loading"
                                }
                            >
                                <nav
                                    slot="navigation"
                                    innerHTML={this.navigationHtml}
                                />
                                <div
                                    slot="navigation-meta"
                                    class="ux-iws-nav__meta"
                                >
                                    <div class="ux-iws-nav__meta-container-header">
                                        <ux-iws-navigation-meta>
                                            {this.renderProfileLink("bottom")}
                                            {this.renderCountryLink("desktop")}
                                            {this.renderHomeLink()}
                                        </ux-iws-navigation-meta>
                                    </div>
                                    <div class="ux-iws-nav__meta-container-burger">
                                        <ux-iws-nav-burger
                                            menu-text-open={menuLabel}
                                            menu-text-close={closeLabel}
                                        ></ux-iws-nav-burger>
                                    </div>
                                </div>
                            </ux-iws-nav>
                        </div>
                    </div>
                    <div
                        slot="header-dropdown"
                        id="ux-iws-header__dropdown-container"
                        class="ux-iws-header__dropdown-container"
                    >
                        <div class="ux-iws-header__meta-container-dropdown">
                            <ux-iws-navigation-meta>
                                {this.renderProfileLink("top")}
                                {this.renderCountryLink("mobile")}
                                <span class="ux-iws-navigation-meta__item__flex-break"></span>
                                {this.renderHomeLink(true)}
                            </ux-iws-navigation-meta>
                        </div>
                    </div>
                </ux-iws-header>
                <div class="is-hidden" aria-hidden="true">
                    <slot name="profile-links" />
                </div>
            </Host>
        );
    }
}
